/**
 * Tabs — token-themed underline tab bar. Controlled: the caller owns the
 * active `value`. Scrolls horizontally rather than wrapping when tabs overflow.
 */

import { type ReactNode } from 'react';

export interface TabItem {
  key: string;
  label: ReactNode;
}

export interface TabsProps {
  tabs: TabItem[];
  value: string;
  onChange: (key: string) => void;
  className?: string;
  'aria-label'?: string;
}

export function Tabs({ tabs, value, onChange, className = '', ...rest }: TabsProps) {
  return (
    <div className={`border-b border-border ${className}`.trim()}>
      <div className="flex gap-1 overflow-x-auto" role="tablist" aria-label={rest['aria-label']}>
        {tabs.map((tab) => {
          const active = tab.key === value;
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onChange(tab.key)}
              className={`group relative -mb-px whitespace-nowrap rounded-t-token px-3.5 py-2.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                active
                  ? 'text-primary'
                  : 'text-muted hover:bg-surface-2/60 hover:text-foreground'
              }`}
            >
              {tab.label}
              {/* Accent underline — grows in from the centre when the tab
                  becomes active (gradient + soft glow, reduced-motion safe). */}
              <span
                aria-hidden="true"
                className={`pointer-events-none absolute inset-x-2 bottom-0 h-[3px] rounded-full bg-gradient-to-r from-primary to-accent transition-all duration-200 ease-out motion-reduce:transition-none ${
                  active
                    ? 'scale-x-100 opacity-100 shadow-[0_0_8px_rgb(var(--color-primary)/0.55)]'
                    : 'scale-x-0 opacity-0'
                }`}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}
